import React, { useEffect, useState } from 'react';
import {
  withLoading,
  useTranslator,
  withMobileAppMode,
  useAppContext,
  callNativeMobileApp,
} from '@front-activerse/common-components';
import { Container, Row, Col, Progress, Alert, Button, Modal } from 'reactstrap';
import { Link } from 'react-router-dom';

import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';

import TrackerPlaceholder from './TrackerPlaceholder';
import TrackerTimeline from './components/TrackerTimeline';
import TrackerProgress from './components/TrackerProgress';
import TrackerActivityComponent from './components/TrackerActivityComponent';
import TrackerMealComponent from './components/TrackerMealComponent';
import TrackerNotificationAlert from './components/TrackerNotificationAlert';

interface RemoteConfigFields {
  tracker_notification_id: number;
  tracker_notification_active: boolean;
}

interface ITrackerContainerProps {
  id: string;
  date: string;
  baseCalories: number;
  trainingCalories: number;
  totalCalories: number;
  consumedCalories: number;
  remainingCalories: number;
  macroSource: string;
  isGoalAchieved: boolean;
  isAvailable: boolean;
  macros: {
    protein: {
      targetGrams: number;
      targetPercentage: number;
      consumedGrams: number;
      remainingGrams: number;
    };
    fat: {
      targetGrams: number;
      targetPercentage: number;
      consumedGrams: number;
      remainingGrams: number;
    };
    carbs: {
      targetGrams: number;
      targetPercentage: number;
      consumedGrams: number;
      remainingGrams: number;
    };
  };
  events: any;
  removeMeal: (mealId: number) => Promise<any>;
  removeActivity: (activityId: number) => Promise<any>;
  prefetchSurroundingDays: () => void;
  trackerArticleId: number;
  notificationData: RemoteConfigFields;
  closedNotifications: number[];
  userId: number;
  getClosedNotificationsValues: () => Promise<void>;
  markNotificationAsClosed: (
    collectionName: string,
    userId: number,
    notificationId: number
  ) => Promise<void>;
  loadingTrackerNotification: boolean;
  mobileAppMode: boolean;
  paidAccess: any;
}

const TrackerContainer: React.FC<ITrackerContainerProps> = ({
  date,
  baseCalories,
  trainingCalories,
  totalCalories,
  consumedCalories,
  remainingCalories,
  macros,
  events,
  removeMeal,
  removeActivity,
  prefetchSurroundingDays,
  isGoalAchieved,
  trackerArticleId,
  notificationData,
  closedNotifications,
  userId,
  getClosedNotificationsValues,
  markNotificationAsClosed,
  loadingTrackerNotification,
  mobileAppMode,
  paidAccess,
}) => {
  const { t } = useTranslator();
  const [showModal, setShowModal] = useState(false);

  const [aiAccess, setAiAccess] = useState(false);
  const [dietAccess, setDietAccess] = useState(false);

  useEffect(() => {
    paidAccess.forEach((access: any) => {
      if (access.accessType === 'ai') {
        setAiAccess(true);
        sessionStorage.setItem('aiAccess', 'true');
      } else if (access.accessType === 'diet') {
        setDietAccess(true);
        sessionStorage.setItem('dietAccess', 'true');
      } else if (access.accessType === 'workout') {
        sessionStorage.setItem('workoutAccess', 'true');
      }
    });
  }, [paidAccess]);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const {
    images: {
      gearIcon,
      addToTrackerIcon,
      mealFromAppIcon,
      describeMealIcon,
      scanFoodIcon,
      scanBarcodeIcon,
      aiBadgeTiny,
    },
  } = useAppContext();

  useEffect(() => {
    prefetchSurroundingDays();
  }, [date]);

  const round = (x: number) => {
    return Math.round(x * 10) / 10;
  };

  const percentageProteins = (macros?.protein?.consumedGrams / macros?.protein?.targetGrams) * 100;
  const percentageFats = (macros?.fat?.consumedGrams / macros?.fat?.targetGrams) * 100;
  const percentageCarbs = (macros?.carbs?.consumedGrams / macros?.carbs?.targetGrams) * 100;

  const reversedEvents = [...events].reverse();

  const renderTrackerNotificationAlert = () => {
    const userClosedNotification = closedNotifications.some(
      (notification) => notification === notificationData.tracker_notification_id
    );

    if (!userClosedNotification && notificationData.tracker_notification_active) {
      return (
        <Row>
          <Col md={{ size: 6, offset: 3 }}>
            <TrackerNotificationAlert
              userId={userId}
              trackerNotificationData={notificationData}
              getClosedNotificationsValues={getClosedNotificationsValues}
              markNotificationAsClosed={markNotificationAsClosed}
            />
          </Col>
        </Row>
      );
    }
    return null;
  };

  const handleAddToTracker = () => {
    if (mobileAppMode) {
      callNativeMobileApp({
        action: 'open_bottom_sheet',
        payload: {
          type: 'tracker_log',
        },
      });
    } else {
      setShowModal(true);
    }
  };

  console.log(paidAccess);

  if (baseCalories === 0) {
    return (
      <Layout>
        <TitleBar showBackBtn={false}>
          {t('tracker.details.navbarTitle')}
          <div className="title-bar-icons">
            <Link to="/tracker/settings" className="ps-2">
              <img src={gearIcon} alt="" style={{ width: '32px', height: '32px' }} />
            </Link>
          </div>
        </TitleBar>

        <div className="page-template-tracker full-height3">
          <Container className="py-xl px-3">
            <Alert color="warning">
              <p className="small">{t('calorieNeeds.update.content')}</p>
              <div className="text-center">
                <Button color="primary" size="sm" tag={Link} to="/tracker/settings">
                  {t('calorieNeeds.error.button')}
                </Button>
              </div>
            </Alert>
          </Container>
        </div>
      </Layout>
    );
  }

  return (
    <>
      <Layout>
        <TitleBar showBackBtn={false}>
          {t('tracker.details.navbarTitle')}
          <div className="title-bar-icons">
            <Link to="/tracker/settings" className="ps-2">
              <img src={gearIcon} alt="" style={{ width: '32px', height: '32px' }} />
            </Link>
          </div>
        </TitleBar>

        <div className="page-template-tracker">
          <Container className="py-md-4">
            <Row>
              <Col xl={{ size: 10, offset: 1 }}>
                <div className="page-container">
                  <TrackerTimeline date={date} />

                  <div className="tracker-container">
                    <p className="small text-text-variant-color mb-m">
                      {t('tracker.dailyGoal.title')}{' '}
                      <span className="text-text-main-color">{baseCalories} kcal</span>
                    </p>

                    <Row className="align-items-center" noGutters>
                      <Col xs="3" className="text-center">
                        <p className="description text-text-main-color mb-0">
                          {consumedCalories > baseCalories
                            ? t('tracker.overLimit.title')
                            : t('tracker.leftCalories.title')}
                        </p>
                        <h5 className="title-small semi-bold mb-0">
                          {consumedCalories > baseCalories
                            ? `+${consumedCalories - baseCalories}`
                            : remainingCalories}
                        </h5>
                        <p className="description text-text-variant-color mb-0">kcal</p>
                      </Col>

                      <Col xs="6">
                        <TrackerProgress
                          unit="kcal"
                          value={consumedCalories}
                          goal={baseCalories}
                          isGoalAchieved={isGoalAchieved}
                        />
                      </Col>

                      <Col xs="3" className="text-center">
                        <p className="description text-text-main-color mb-0">
                          {t('tracker.burnedCalories.title')}
                        </p>
                        <h5 className="title-small semi-bold mb-0">{trainingCalories}</h5>
                        <p className="description text-text-variant-color mb-0">kcal</p>
                      </Col>
                    </Row>
                    <Row className="mt-s mb-xl">
                      <Col>
                        <Link
                          to="/tracker/settings/macro"
                          state={{
                            from: {
                              pathname: '/tracker/settings/macro',
                            },
                          }}
                        >
                          <div className="white-box position-relative">
                            <Row>
                              <Col>
                                <p className="description text-text-variant-color mb-xxs">
                                  {t('tracker.marcosProtein.title')}
                                </p>
                                <Progress
                                  className="progress-proteins mb-xxs"
                                  value={percentageProteins}
                                />
                                <p className="mb-0 small text-text-main-color">
                                  {round(macros?.protein?.consumedGrams)} /{' '}
                                  {round(macros?.protein?.targetGrams)} g
                                </p>
                              </Col>
                              <Col>
                                <p className="description text-text-variant-color mb-xxs">
                                  {t('mealPlan.nutritionalInfo.fats.title')}
                                </p>
                                <Progress className="progress-fats mb-xxs" value={percentageFats} />
                                <p className="mb-0 small text-text-main-color">
                                  {round(macros?.fat?.consumedGrams)} /{' '}
                                  {round(macros?.fat?.targetGrams)} g
                                </p>
                              </Col>
                              <Col>
                                <p className="description text-text-variant-color mb-xxs">
                                  {t('mealPlan.nutritionalInfo.carbs.title')}
                                </p>
                                <Progress
                                  className="progress-carbs mb-xxs"
                                  value={percentageCarbs}
                                />
                                <p className="mb-0 small text-text-main-color">
                                  {round(macros?.carbs?.consumedGrams)} /{' '}
                                  {round(macros?.carbs?.targetGrams)} g
                                </p>
                              </Col>
                            </Row>

                            <img
                              src={gearIcon}
                              alt=""
                              className="position-absolute"
                              style={{ width: '14px', height: '14px', top: '2px', right: '2px' }}
                            />
                          </div>
                        </Link>
                      </Col>
                    </Row>

                    <div className="text-center">
                      <Button
                        color="tracker-animated"
                        size="sm"
                        onClick={() => handleAddToTracker()}
                      >
                        <span>
                          <img
                            src={addToTrackerIcon}
                            alt={t('tracker.meal.addToTrackerButton')}
                            className="me-2"
                          />
                          {t('tracker.details.addButton')}
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>

                {/* History */}
                <div className="tracker-history">
                  {/* Macro settings alert */}
                  {!loadingTrackerNotification && renderTrackerNotificationAlert()}

                  {reversedEvents.length > 0 ? (
                    <>
                      <h3 className="title-extra-small semi-bold mb-m">
                        {t('tracker.history.title')}
                      </h3>

                      <div className="tracker-history-list">
                        {reversedEvents.map((event: any) => {
                          if (event.__typename === 'SimpleDailyTrackerActivity') {
                            return (
                              <TrackerActivityComponent
                                {...event}
                                removeActivity={removeActivity}
                                date={date}
                              />
                            );
                          } else if (event.__typename === 'DailyTrackerMealWithProducts') {
                            return (
                              <TrackerMealComponent
                                {...event}
                                removeMeal={removeMeal}
                                date={date}
                              />
                            );
                          }
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="no-history">
                      <h3 className="title-extra-small semi-bold mb-s">
                        {t('tracker.history.noHistory.title')}
                      </h3>

                      <p className="small text-text-variant-color mb-0">
                        {t('tracker.history.noHistory.description')}
                      </p>
                    </div>
                  )}

                  <hr />

                  <p className="description text-text-variant-color mb-s">
                    {t('tracker.sourceInfo.content')}
                    <br />
                  </p>
                  <p className="description">
                    <Link to={`/knowledge/${trackerArticleId}`}>
                      {t('tracker.sourceInfo.button')}
                    </Link>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </Layout>

      <Modal
        className="from-bottom"
        isOpen={showModal}
        fullscreen={true}
        size="lg"
        toggle={toggleModal}
      >
        <div className="select-insert-date">
          <p className="mb-0">{t('actionSheet.logToTracker.title')}</p>

          <Container>
            <Row>
              <Col xs="12" md={{ size: 4, offset: 4 }}>
                <Button
                  color="tracker"
                  className="w-100"
                  tag={Link}
                  to={`/tracker/scan-plate`}
                  onClick={toggleModal}
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div>
                      <img src={scanFoodIcon} alt={t('actionSheet.logToTracker.option.scan')} />
                      {t('actionSheet.logToTracker.option.scan.food')}
                    </div>
                    <div className="text-end">
                      <img src={aiBadgeTiny} alt="" className="ai-badge" />
                      {!aiAccess && (
                        <span className="mt-1">{t('actionSheet.label.getAccess')}</span>
                      )}
                    </div>
                  </div>
                </Button>

                <Button
                  color="tracker"
                  className="w-100"
                  tag={Link}
                  to={`/tracker/describe-meal`}
                  onClick={toggleModal}
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div>
                      <img
                        src={describeMealIcon}
                        alt={t('actionSheet.logToTracker.option.describeMeal')}
                      />
                      {t('actionSheet.logToTracker.option.describeMeal')}
                    </div>
                    <div className="text-end">
                      <img src={aiBadgeTiny} alt="" className="ai-badge" />
                      {!aiAccess && (
                        <span className="mt-1">{t('actionSheet.label.getAccess')}</span>
                      )}
                    </div>
                  </div>
                </Button>

                <Button
                  color="tracker"
                  className="w-100"
                  tag={Link}
                  to={`/tracker/scan-barcode`}
                  onClick={toggleModal}
                >
                  <div>
                    <img
                      src={scanBarcodeIcon}
                      alt={t('actionSheet.logToTracker.option.scan.barcode')}
                    />
                    {t('actionSheet.logToTracker.option.scan.barcode')}
                  </div>
                  <div className="text-end">
                    {!aiAccess && !dietAccess && (
                      <span className="mt-1">{t('actionSheet.label.getAccess')}</span>
                    )}
                  </div>
                </Button>

                <Button color="tracker" tag={Link} to="/" className="w-100">
                  <div>
                    <img
                      src={mealFromAppIcon}
                      alt={t('actionSheet.logToTracker.option.mealFromApp')}
                    />
                    {t('actionSheet.logToTracker.option.mealFromApp')}
                  </div>
                  <div className="text-end">
                    {!dietAccess && (
                      <span className="mt-1">{t('actionSheet.label.getAccess')}</span>
                    )}
                  </div>
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      </Modal>
    </>
  );
};

const Placeholder = ({ date, loading }: { date: string; loading: boolean }) => (
  <TrackerPlaceholder date={date} loading={loading} />
);

export default withLoading(withMobileAppMode(TrackerContainer), Placeholder);
